import { useState } from 'react';
import { useT, type TKey } from '../i18n';
import { useCatStore } from '../catStore';
import { api } from '../api';
import { ApiClientError } from '../api/client';
import { CAT_CLASSES, SCAR_DEFS, MUTATION_DEFS } from '@cradle7/shared';
import { haptic } from '../telegram';
import type { CatRecord } from '@cradle7/shared';

type Props = {
  cat: CatRecord | null;
  onDismiss: () => void;
};

export function CatRetiredModal({ cat, onDismiss }: Props) {
  const { t } = useT();
  const [adopting, setAdopting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!cat) return null;

  const klass = CAT_CLASSES.find((c) => c.id === cat.classId);
  const className = klass ? t(klass.nameKey as TKey) : cat.classId;

  async function onAdopt() {
    if (!cat) return;
    setAdopting(true);
    setError(null);
    haptic.tick();
    try {
      const result = await api.adoptCat(cat.classId);
      useCatStore.getState().hydrateFromServer(result);
      haptic.notify('success');
      onDismiss();
    } catch (err: unknown) {
      haptic.notify('error');
      if (err instanceof ApiClientError) {
        setError(err.code === 'cat_already_active' ? t('cats.error.alreadyActive' as TKey) : t('cats.error.generic' as TKey));
      } else {
        setError(t('cats.error.generic' as TKey));
      }
    } finally {
      setAdopting(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 16, 0.85)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 'var(--space-4)',
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="terminal-frame fade-in" style={{
        maxWidth: '24rem',
        width: '100%',
        padding: 'var(--space-4)',
        position: 'relative',
      }}>
        <span className="corner-tr" />
        <span className="corner-bl" />
        <h2 className="subheadline glow-danger" style={{ textAlign: 'center', marginBottom: 'var(--space-3)' }}>
          🪦 {t('cats.retired.heading' as TKey).replace('{class}', className)}
        </h2>
        <div style={{
          fontSize: '0.7rem',
          color: 'var(--fg-text-dim)',
          lineHeight: 1.6,
          marginBottom: 'var(--space-3)',
        }}>
          <div>{t('cats.runsPlayed' as TKey).replace('{n}', String(cat.runsPlayed))}</div>
          {cat.scars.length > 0 && (
            <div style={{ color: 'var(--fg-danger)', marginTop: 4 }}>
              {t('cats.scarsCount' as TKey).replace('{n}', String(cat.scars.length))}: {cat.scars.map((s) => {
                const def = SCAR_DEFS.find((d) => d.id === s);
                return def ? t(def.nameKey as TKey) : s;
              }).join(', ')}
            </div>
          )}
          {cat.mutations.length > 0 && (
            <div style={{ color: 'var(--fg-success)', marginTop: 4 }}>
              {t('cats.mutationsCount' as TKey).replace('{n}', String(cat.mutations.length))}: {cat.mutations.map((m) => {
                const def = MUTATION_DEFS.find((d) => d.id === m);
                return def ? t(def.nameKey as TKey) : m;
              }).join(', ')}
            </div>
          )}
        </div>
        {error && (
          <div role="alert" style={{ color: 'var(--fg-danger)', fontSize: '0.7rem', marginBottom: 'var(--space-2)' }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            onClick={onAdopt}
            disabled={adopting}
            data-testid="cat-retired-adopt"
            style={{
              flex: 1,
              padding: '10px',
              background: 'var(--fg-secondary)',
              color: '#000',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 'bold',
              letterSpacing: '0.08em',
            }}
          >
            {adopting ? '…' : t('cats.adoptNew' as TKey)}
          </button>
          <button
            onClick={onDismiss}
            data-testid="cat-retired-dismiss"
            style={{
              padding: '10px 12px',
              background: 'transparent',
              color: 'var(--fg-muted)',
              border: '1px solid var(--fg-primary-dim)',
              cursor: 'pointer',
              fontSize: '0.75rem',
            }}
          >
            {t('cats.dismiss' as TKey)}
          </button>
        </div>
      </div>
    </div>
  );
}

